<template>
  <div class="leave-detail-page">
    <loading
      :active.sync="loading"
      :can-cancel="false"
      :is-full-page="true"
      :height="120"
      :width="120"
      :color="'#4F6BFF'"
      :background-color="'#0F172A'"
      :opacity="0.6"
    ></loading>

    <v-container fluid class="pa-6">

      <!-- Page Header -->
      <div class="detail-header mb-6">
        <v-btn icon class="back-btn mr-3" @click="backHandler">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <p class="header-breadcrumb">{{ $t('whoisonleave') }}</p>
          <h1 class="header-title">{{ pageTitle }}</h1>
          <p class="header-staff">{{ pageTitleValue }}</p>
        </div>
        <div class="ml-auto">
          <span
            class="leave-status-badge"
            :class="formData.lvlstatusholder === '(A)' ? 'badge-approved' : formData.lvlstatusholder === '(P)' ? 'badge-pending' : 'badge-other'"
          >
            <v-icon size="13" class="mr-1">
              {{ formData.lvlstatusholder === '(A)' ? 'mdi-check-circle' : 'mdi-clock-outline' }}
            </v-icon>
            {{ formData.lvlstatusholder === '(A)' ? 'Approved' : formData.lvlstatusholder === '(P)' ? 'Pending' : formData.lvlstatusholder }}
          </span>
        </div>
      </div>

      <!-- Leave Info Card -->
      <div class="info-card mb-5">
        <div class="info-card-header">
          <v-icon size="18" color="#4F6BFF" class="mr-2">mdi-calendar-range</v-icon>
          <span>{{ $t('isonleave.leaveinfo') }}</span>
        </div>
        <div class="info-card-body">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.fromdate') }}</span>
                <span class="info-value">
                  <v-icon size="15" color="#4F6BFF" class="mr-1">mdi-calendar-start</v-icon>
                  {{ formData.startdate }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.todate') }}</span>
                <span class="info-value">
                  <v-icon size="15" color="#7C3AFF" class="mr-1">mdi-calendar-end</v-icon>
                  {{ formData.enddate }}
                </span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.reason') }}</span>
                <span class="info-value">{{ formData.reasondesc || '—' }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.relperson') }}</span>
                <span class="info-value">{{ formData.reliefname || '—' }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.clinic') }}</span>
                <span class="info-value">{{ formData.clinicdesc || '—' }}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-field">
                <span class="info-label">{{ $t('columns.remark') }}</span>
                <span class="info-value">{{ formData.lvremarks || '—' }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- Attachment -->
          <div v-if="formData.attachment" class="attachment-row mt-2">
            <span class="info-label">{{ $t('recstaffdetails.attachment') }}</span>
            <a :href="formData.attachment" :download="'Download'" class="attachment-link">
              <div class="attachment-box">
                <v-icon color="#4F6BFF" class="mr-2">mdi-paperclip</v-icon>
                <span>Download Attachment</span>
                <v-icon size="16" color="#4F6BFF" class="ml-auto">mdi-download</v-icon>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Approval Info Expandable -->
      <div class="info-card">
        <v-expansion-panels flat>
          <v-expansion-panel class="expansion-clean">
            <v-expansion-panel-header class="info-card-header expansion-header">
              <div class="d-flex align-center">
                <v-icon size="18" color="#4F6BFF" class="mr-2">mdi-shield-check-outline</v-icon>
                <span>{{ $t('recleaverec.entry_apprvlinfo') }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="info-card-body pt-0">

                <!-- Entry Info -->
                <p class="section-subtitle">{{ $t('recleaverec.leaveentryinfo') }}</p>
                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="info-field">
                      <span class="info-label">{{ $t('recleaverec.submittedby') }}</span>
                      <span class="info-value">{{ formData.entrybyusername || '—' }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="info-field">
                      <span class="info-label">{{ $t('preapprovedotrecord.submissiontime') }}</span>
                      <span class="info-value">{{ formData.entrytime || '—' }}</span>
                    </div>
                  </v-col>
                </v-row>

                <!-- Approval Info -->
                <p class="section-subtitle mt-4">{{ $t('recleaverec.leaveapprvlinfo') }}</p>
                <div
                  v-for="(item, index) in leaveApprInfo"
                  :key="index"
                  class="approval-card mb-3"
                >
                  <div class="approval-card-inner">
                    <div class="approval-row">
                      <span class="info-label">{{ $t('recleaverec.actiontime') }}</span>
                      <span class="info-value">{{ item.aprdate }}</span>
                    </div>
                    <div class="approval-row">
                      <span class="info-label">{{ $t('recleaverec.actby') }}</span>
                      <span class="info-value">{{ item.staffcode }} – {{ item.psnname }}</span>
                    </div>
                    <div class="approval-row">
                      <span class="info-label">{{ $t('columns.status') }}</span>
                      <span class="aprstatus-chip">{{ item.aprstatus }}</span>
                    </div>
                    <div class="approval-row" v-if="item.remark">
                      <span class="info-label">{{ $t('recleaverec.apprvlremark') }}</span>
                      <span class="info-value remark-text">{{ item.remark }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="leaveApprInfo.length === 0" class="empty-approvals">
                  <v-icon color="#94a3b8" class="mr-2">mdi-information-outline</v-icon>
                  No approval records found.
                </div>

              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import Loading from "vue-loading-overlay";

export default {
  components: { Loading },

  data() {
    return {
      publicPath: process.env.BASE_URL,
      rules: {
        required: (value) => !!value || this.$t('reportsparam.required'),
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || this.$t('reportsparam.invalidemail');
        },
      },

      pageTitle: this.$route.params.item.leavedesc,
      pageTitleValue: this.$route.params.item.staffcode + ' - ' + this.$route.params.item.psnname,
      data: this.$route.params.item,

      formData: {
        id: this.$route.params.item.id,
        appsrcid: this.$route.params.appsrcid,
        aprvremark: this.$route.params.item.aprvremark,
        attachment: this.$route.params.item.attachment,
        cliniccode: this.$route.params.item.cliniccode,
        clinicdesc: this.$route.params.item.clinicdesc,
        clinicid: this.$route.params.item.clinicid,
        enddate: moment(this.$route.params.item.enddate).format('DD-MM-YYYY'),
        entrybyid: this.$route.params.item.entrybyid,
        entrybyusername: this.$route.params.item.entrybyusername,
        entrytime: moment(this.$route.params.item.entrytime).format('DD-MM-YYYY hh:mm'),
        ishistory: this.$route.params.item.ishistory,
        leavecode: this.$route.params.item.leavecode,
        leavedesc: this.$route.params.item.leavedesc,
        leavetypeid: this.$route.params.item.leavetypeid,
        lvdays: this.$route.params.item.lvdays,
        lvlstatusholder: this.$route.params.item.lvlstatusholder,
        lvremarks: this.$route.params.item.lvremarks,
        lvstatusid: this.$route.params.item.lvstatusid,
        poid: this.$route.params.item.poid,
        posted: this.$route.params.item.posted,
        psnid: this.$route.params.item.psnid,
        psnname: this.$route.params.item.psnname,
        reasoncode: this.$route.params.item.reasoncode,
        reasondesc: this.$route.params.item.reasondesc,
        reasonid: this.$route.params.item.reasonid,
        rectimestamp: this.$route.params.item.rectimestamp,
        reliefname: this.$route.params.item.reliefname,
        reliefpsnid: this.$route.params.item.reliefpsnid,
        reliefstaffcode: this.$route.params.item.reliefstaffcode,
        reliefstaffid: this.$route.params.item.reliefstaffid,
        src: this.$route.params.item.src,
        staffcode: this.$route.params.item.staffcode,
        staffid: this.$route.params.item.staffid,
        startdate: moment(this.$route.params.item.startdate).format('DD-MM-YYYY'),
      },

      leaveApprInfo: [],
      loading: false,
    };
  },

  methods: {
    loadLeaveAprInfo() {
      this.loading = true;
      if (this.formData.id) {
        return this.$store.dispatch('get', {url: "/leaverecord/wftran/" + this.formData.id})
          .then((res) => {
            this.leaveApprInfo = res.data.rec;
            if (this.leaveApprInfo.length > 0) {
              for (var i = 0; i < this.leaveApprInfo.length; i++) {
                this.leaveApprInfo[i].aprdate = moment(this.leaveApprInfo[i].aprdate).format("YYYY-MM-DD HH:mm:ss");
              }
            }
            this.loading = false;
          });
      }
    },

    backHandler() {
      this.$router.push({name: 'whoisonleavedashboard'});
    },
  },

  async mounted() {
    await this.loadLeaveAprInfo();
  },
};
</script>

<style>
.leave-detail-page {
  background: #F0F4FF;
  min-height: 100vh;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

/* ---- Header ---- */
.detail-header {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.back-btn {
  background: #fff !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
  border-radius: 10px !important;
  margin-top: 6px;
  flex-shrink: 0;
}
.header-breadcrumb {
  font-size: 0.72rem;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 2px;
  font-weight: 600;
}
.header-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 2px;
  line-height: 1.2;
}
.header-staff {
  font-size: 0.88rem;
  color: #64748B;
  margin: 0;
}
.leave-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 5px 14px;
}
.badge-approved {
  background: #D1FAE5;
  color: #065F46;
}
.badge-pending {
  background: #FEF3C7;
  color: #92400E;
}
.badge-other {
  background: #E0E7FF;
  color: #3730A3;
}

/* ---- Info Card ---- */
.info-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1px solid #E8EDF5;
  overflow: hidden;
}
.info-card-header {
  background: #F8FAFF;
  border-bottom: 1px solid #E8EDF5;
  padding: 14px 20px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #334155;
  display: flex;
  align-items: center;
}
.info-card-body {
  padding: 20px;
}
.info-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 0;
  border-bottom: 1px solid #F1F5F9;
}
.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94A3B8;
}
.info-value {
  font-size: 0.9rem;
  color: #334155;
  font-weight: 500;
  display: flex;
  align-items: center;
}

/* ---- Attachment ---- */
.attachment-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.attachment-link {
  text-decoration: none;
}
.attachment-box {
  display: flex;
  align-items: center;
  background: #F0F4FF;
  border: 1.5px dashed #A5B4FC;
  border-radius: 10px;
  padding: 10px 14px;
  color: #4F6BFF;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 0.15s;
  cursor: pointer;
}
.attachment-box:hover {
  background: #E0E7FF;
}

/* ---- Expansion Panel ---- */
.expansion-clean {
  border: none !important;
  box-shadow: none !important;
}
.expansion-header {
  border-bottom: none !important;
  padding: 0 20px !important;
  min-height: 52px !important;
}
.section-subtitle {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #4F6BFF;
  margin-bottom: 8px;
}

/* ---- Approval Card ---- */
.approval-card {
  border-radius: 12px;
  border: 1px solid #E8EDF5;
  overflow: hidden;
}
.approval-card-inner {
  padding: 14px 16px;
}
.approval-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #F1F5F9;
}
.approval-row:last-child {
  border-bottom: none;
}
.aprstatus-chip {
  font-size: 0.75rem;
  font-weight: 700;
  background: #EEF2FF;
  color: #4F6BFF;
  border-radius: 20px;
  padding: 3px 12px;
}
.remark-text {
  font-style: italic;
  color: #64748B;
}
.empty-approvals {
  display: flex;
  align-items: center;
  color: #94a3b8;
  font-size: 0.85rem;
  padding: 12px 0;
}
</style>